// Tracker board logic (KOP-38 spec §5). Pure: no Vue, no axios. `today` is always injected.
import { isoToRu, unixToRu, isOverdue } from './format';
import { anchorPrinter, hasValidCoords } from './objectState';
import {
  isIsoDate, shiftIso, isValidPeriod, samePeriod, periodQuery, readPeriod as readPeriodWith,
} from './period';

export {
  isIsoDate, shiftIso, isValidPeriod, samePeriod, periodQuery,
};

/* ---------- period ---------- */

export function defaultPeriod(today) {
  return { from: shiftIso(today, -7), to: today };
}

export function readPeriod(query, today) {
  return readPeriodWith(query, defaultPeriod(today));
}

export const COLUMNS = [
  { key: 'assigned', title: 'Назначены', tone: 'grey' },
  { key: 'done', title: 'Выполнены', tone: 'green' },
  { key: 'partial', title: 'Не выполнены частично', tone: 'amber' },
  { key: 'blocked', title: 'Отменены', tone: 'red' },
];

const ASSIGNED_CODES = ['assigned', 'in_progress'];

const works = (task) => (task && Array.isArray(task.field_works) ? task.field_works : []);
export const hasUnfinishedWorks = (task) => works(task).some((w) => w && w.is_done === false);

/* ---------- columns ---------- */

export function columnOf(task) {
  const code = task && task.status && task.status.code;
  if (ASSIGNED_CODES.includes(code)) return 'assigned';
  if (code === 'blocked') return 'blocked';
  if (code !== 'done') return null;
  return hasUnfinishedWorks(task) ? 'partial' : 'done';
}

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const byIdDesc = (a, b) => Number(b.id) - Number(a.id);
// Missing keys sort last; ties fall back to the newest id first.
const byDeadlineAsc = (a, b) => {
  const da = a.deadline_date || null;
  const db = b.deadline_date || null;
  if (da === db) return byIdDesc(a, b);
  if (da === null) return 1;
  if (db === null) return -1;
  return da < db ? -1 : 1;
};
const descBy = (field) => (a, b) => {
  const ca = num(a[field]);
  const cb = num(b[field]);
  if (ca === cb) return byIdDesc(a, b);
  if (ca === null) return 1;
  if (cb === null) return -1;
  return cb - ca;
};
const byCompletedDesc = descBy('completed_at');
const byUpdatedDesc = descBy('updated_at');

export function bucketTasks(tasks) {
  const out = { assigned: [], done: [], partial: [], blocked: [] };
  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const key = columnOf(task);
    if (key) out[key].push(task);
  });
  out.assigned.sort(byDeadlineAsc);
  out.done.sort(byCompletedDesc);
  out.partial.sort(byCompletedDesc);
  out.blocked.sort(byUpdatedDesc);
  return out;
}

export const REOPEN_TASK_OK = 'Открыть повторно';
export const reopenTaskText = (id) => `Открыть задачу T‑${id} повторно? Она вернётся в «Назначены», отметки о выполнении сохранятся, комментарий инженера будет удалён.`;

export const RESUME_TASK_OK = 'Вернуть в работу';
export const resumeTaskText = (id) => `Вернуть задачу T‑${id} в работу? Она вернётся в «Назначены», отметки о выполнении сохранятся.`;

const trim = (v) => String(v == null ? '' : v).trim();

function printerModel(location) {
  const p = location ? anchorPrinter(location) : null;
  if (!p) return '';
  return trim(p.unit && p.unit.name) || trim(p.printer && p.printer.name);
}

/* ---------- card view-model (spec §5.3) ---------- */

export function cardModel(task, locationsById, today) {
  const column = columnOf(task);
  let kind = 'closed';
  if (column === 'assigned' || column === 'blocked') kind = column;
  const enriched = (locationsById && locationsById[task.geo_location_id]) || null;
  const own = task.geo_location || null;
  const objectName = trim((enriched && enriched.name) || (own && own.name));
  const model = printerModel(enriched);
  const fallback = trim(task.title) || `T‑${task.id}`;
  let title = fallback;
  if (objectName) title = model ? `${model} · ${objectName}` : objectName;
  const assignee = task.assignee || null;
  const closedAt = kind === 'closed' ? unixToRu(task.completed_at) : null; // '—' for legacy tasks without completed_at
  const blockedAt = kind === 'blocked' ? unixToRu(task.updated_at) : null; // blocking leaves no own timestamp
  return {
    id: task.id,
    kind,
    overdue: kind === 'assigned' && isOverdue(task.deadline_date, today),
    title,
    objectName,
    assignee: trim(assignee && assignee.username) || trim(assignee && assignee.email) || '—',
    deadline: isoToRu(task.deadline_date),
    closedAt: closedAt === '—' ? null : closedAt,
    blockedAt: blockedAt === '—' ? null : blockedAt,
    works: works(task).map((w) => ({ title: trim(w && w.type && w.type.title) || '—', done: !!(w && w.is_done) })),
    dispatcherComment: trim(task.description),
    engineerComment: kind === 'assigned' ? '' : trim(task.completion_comment),
    geoLocationId: task.geo_location_id,
    canFocus: hasValidCoords(enriched) || hasValidCoords(own),
  };
}
